import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  Search,
  Loader2,
  Crown,
  Briefcase,
  GraduationCap,
  Store,
  MapPin,
  Network,
  Building2,
  ChevronRight,
  ChevronDown,
  Mail,
  Phone,
  Award,
  BookOpen,
  TrendingUp,
  Eye,
  UserCircle,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role icons mapping
const ROLE_INFO = {
  admin: { name: 'Administrator', icon: Crown, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
  training_team: { name: 'L&D Admin', icon: GraduationCap, color: 'text-purple-500', bgColor: 'bg-purple-500/10' },
  regional_manager: { name: 'Regional Manager', icon: MapPin, color: 'text-green-500', bgColor: 'bg-green-500/10' },
  store_manager: { name: 'Store Manager', icon: Store, color: 'text-blue-500', bgColor: 'bg-blue-500/10' },
  cra: { name: 'Store Executive', icon: Briefcase, color: 'text-gray-500', bgColor: 'bg-gray-500/10' },
};

const MyTeam = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data states
  const [teamData, setTeamData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [memberProgress, setMemberProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/org/my-team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamData(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberProgress = async (memberId) => {
    try {
      setLoadingProgress(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users/${memberId}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => ({ data: null }));
      setMemberProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setLoadingProgress(false);
    }
  };

  const openMemberDetails = async (member) => {
    setSelectedMember(member);
    setMemberDialogOpen(true);
    await fetchMemberProgress(member.id);
  };

  const getRoleInfo = (roleId) => ROLE_INFO[roleId] || ROLE_INFO.cra;

  const filteredMembers = teamData?.team_members?.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return member.name?.toLowerCase().includes(query) ||
      member.email?.toLowerCase().includes(query) ||
      member.employee_id?.toLowerCase().includes(query);
  }) || [];

  // Group members by their org entry
  const groupedMembers = filteredMembers.reduce((acc, member) => {
    const entryName = member.org_entry?.entry_name || 'Unassigned';
    if (!acc[entryName]) acc[entryName] = [];
    acc[entryName].push(member);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: branding.primary_color }} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="my-team-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-7 h-7" style={{ color: branding.primary_color }} />
              {t('nav.myTeam', 'My Team')}
            </h1>
            <p className="text-muted-foreground">{t('myTeam.subtitle', 'View and manage your team members')}</p>
          </div>
        </div>

        {/* Your Position Card */}
        {teamData?.user_entry && (
          <Card className="border-l-4" style={{ borderLeftColor: branding.primary_color }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full" style={{ backgroundColor: `${branding.primary_color}20` }}>
                  <Network className="w-6 h-6" style={{ color: branding.primary_color }} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('myTeam.yourPosition', 'Your Position')}</p>
                  <p className="font-semibold text-lg">{teamData.user_entry.entry_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {teamData.user_entry.entry_code} • {t('myTeam.level', 'Level')} {teamData.user_entry.level_number}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full" style={{ backgroundColor: `${branding.primary_color}20` }}>
                <Users className="w-5 h-5" style={{ color: branding.primary_color }} />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamData?.total_team_size || 0}</p>
                <p className="text-xs text-muted-foreground">{t('myTeam.teamMembers', 'Team Members')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/10">
                <Building2 className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{teamData?.downline_entries?.length || 0}</p>
                <p className="text-xs text-muted-foreground">{t('myTeam.downlinePositions', 'Downline Positions')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-500/10">
                <Store className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {new Set(filteredMembers.flatMap(m => m.assigned_store_ids || [])).size}
                </p>
                <p className="text-xs text-muted-foreground">{t('myTeam.storesCovered', 'Stores Covered')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-500/10">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">-</p>
                <p className="text-xs text-muted-foreground">{t('myTeam.avgCompletion', 'Avg. Completion')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Team Members */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>{t('myTeam.teamMembersTitle', 'Team Members')}</CardTitle>
                <CardDescription>{t('myTeam.teamMembersDesc', 'People reporting to you in the organization')}</CardDescription>
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('myTeam.searchPlaceholder', 'Search team members...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredMembers.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-6">
                  {Object.entries(groupedMembers).map(([entryName, members]) => (
                    <div key={entryName}>
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">{entryName}</span>
                        <Badge variant="secondary" className="text-xs">{members.length}</Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {members.map((member) => {
                          const roleInfo = getRoleInfo(member.role);
                          return (
                            <Card 
                              key={member.id} 
                              className="cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => openMemberDetails(member)}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <Avatar className="h-12 w-12">
                                    <AvatarImage src={member.avatar} />
                                    <AvatarFallback className={roleInfo.bgColor}>
                                      {member.name?.charAt(0)?.toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{member.name}</p>
                                    <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge variant="outline" className={`text-xs ${roleInfo.bgColor}`}>
                                        <roleInfo.icon className={`w-3 h-3 mr-1 ${roleInfo.color}`} />
                                        {roleInfo.name}
                                      </Badge>
                                    </div>
                                    {member.stores?.length > 0 && (
                                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                        <Store className="w-3 h-3" />
                                        {member.stores.length} store{member.stores.length > 1 ? 's' : ''}
                                      </div>
                                    )}
                                  </div>
                                  <Button variant="ghost" size="sm" className="flex-shrink-0">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">{t('myTeam.noTeamMembers', 'No Team Members')}</h3>
                <p className="text-muted-foreground">
                  {searchQuery 
                    ? t('myTeam.noSearchResults', 'No team members match your search.')
                    : t('myTeam.noTeamMembersDesc', "You don't have any team members reporting to you yet.")}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Member Details Dialog */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('myTeam.memberDetails', 'Team Member Details')}</DialogTitle>
            </DialogHeader>
            {selectedMember && (
              <div className="space-y-4">
                {/* Profile Header */}
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={selectedMember.avatar} />
                    <AvatarFallback className={getRoleInfo(selectedMember.role).bgColor}>
                      {selectedMember.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg">{selectedMember.name}</h3>
                    <Badge variant="outline" className={`${getRoleInfo(selectedMember.role).bgColor} mt-1`}>
                      {getRoleInfo(selectedMember.role).name}
                    </Badge>
                  </div>
                </div>

                <Separator />

                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedMember.email}</span>
                  </div>
                  {selectedMember.mobile_number && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedMember.mobile_number}</span>
                    </div>
                  )}
                  {selectedMember.employee_id && (
                    <div className="flex items-center gap-3 text-sm">
                      <UserCircle className="w-4 h-4 text-muted-foreground" />
                      <span>ID: {selectedMember.employee_id}</span>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Organization Info */}
                {selectedMember.org_entry && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Organization Position</p>
                    <div className="flex items-center gap-2">
                      <Network className="w-4 h-4" style={{ color: branding.primary_color }} />
                      <span className="font-medium">{selectedMember.org_entry.entry_name}</span>
                      <Badge variant="outline" className="text-xs">
                        {selectedMember.org_entry.entry_code}
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Assigned Stores */}
                {selectedMember.stores?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Assigned Stores ({selectedMember.stores.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedMember.stores.map(store => (
                        <Badge key={store.id} variant="secondary" className="text-xs">
                          <Store className="w-3 h-3 mr-1" />
                          {store.store_name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Learning Progress */}
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">Learning Progress</p>
                  {loadingProgress ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : memberProgress ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <BookOpen className="w-4 h-4 text-blue-500" />
                          Courses Completed
                        </span>
                        <span className="font-medium">{memberProgress.completed_courses || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-yellow-500" />
                          Points Earned
                        </span>
                        <span className="font-medium">{memberProgress.points || 0}</span>
                      </div>
                      {memberProgress.current_course && (
                        <div className="bg-muted rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">Currently Learning</p>
                          <p className="text-sm font-medium">{memberProgress.current_course.title}</p>
                          <Progress value={memberProgress.current_course.progress || 0} className="h-1.5 mt-2" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No learning data available</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMemberDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MyTeam;
